import type { ProductBase } from '../../../types/productBase'
import type { ProductConfigurableGtm } from '~/types/ProductConfigurableGtm'
import { BbkCustomerDataLayer } from '../../../graphql/types/graphql'

import { getGtmFlagIdentifier } from '../utils/getGtmFlagIdentifier'
import { getGtmLabels } from '../utils/getGtmLabels'
import { getGtmProductUrl } from '../utils/getGtmProductUrl'

type TypeRelatedProductLoadedEvent = {
    products: ReturnType<typeof getProductRelatedForGtm>[]
    [key: string]: unknown
}

export function useGtmProduct() {
    const gtm = useGtm()
    const { $i18n } = useNuxtApp()
    const { siteUrl } = useRuntimeConfig().public
    const { BRAND_BBK } = useAppConfig()

    const productPageLoadedEvent = async (
        product: ProductBase,
        isConfigParent: boolean,
        parent: { sku: string },
        gaCookie: string,
        currentStore: string,
        customerData: BbkCustomerDataLayer,
    ) => {
        const { isUserLoggedIn } = useUser()
        const { finalPrice, regularPrice, currencyCode, priceDiscount } = usePrice(product.productPrice)
        const flagIdentifier = getGtmFlagIdentifier(product)
        const productUrl = getGtmProductUrl(product)

        const imageUrl =
            (product?.gallery && product.gallery.length > 0 ? product.gallery[0].src : null) ||
            (product?.images && product.images.length > 0 ? product.images[0].src : '') || '';

        gtm?.trackEvent({
            event: 'productDetail',
            ecommerce: {
                currencyCode,
                detail: {
                    products: [
                        {
                            backInStock: +flagIdentifier.includes('backInStock'),
                            bestseller: +flagIdentifier.includes('bestseller'),
                            brand: product.brandLabel,
                            categoryId: product.categoryId || product.dlCategoryIdentifier || '',
                            categoryName: product.categoryName || product.dlCategoryName || '',
                            categoryHierarchyId: product.categoryHierarchyId || product.dlCategoryIdentifierPath || '',
                            dimension: $i18n.t('Bbk.gtm.inStorage'),
                            freeShipping: +flagIdentifier.includes('free-delivery'),
                            function: product.function || '',
                            id: parent ? parent.sku : product.sku,
                            ...(parent && { variantId: product.sku }),
                            imageUrl,
                            inStock: +product.inStock || product.stockStatus === 'IN_STOCK' || '',
                            inPromotion: +flagIdentifier.includes('discount'),
                            lowestPriceHistory: product.lowestPrice?.value || null,
                            name: product.name,
                            new: +flagIdentifier.includes('new'),
                            productId: product.id,
                            parentCategoryId: product.parentCategoryId || product.dlParentCategoryIdentifier || '',
                            parentCategoryName: product.parentCategoryName || product.dlParentCategoryName || '',
                            price: priceDiscount || regularPrice,
                            priceCatalog: regularPrice,
                            productType: product.typeId,
                            productFunctionPL: product.function,
                            properName: product.properName,
                            seller: product.shopName || BRAND_BBK,
                            url: `${siteUrl}/${currentStore}${productUrl}`,
                            fullCategoryString: product?.dataLayer?.fullCategoryString,
                            topCategoryId: product?.dataLayer?.topCategoryId,
                            topCategoryName: product?.dataLayer?.topCategoryName,
                        },
                    ],
                },
            },
        })

        gtm?.trackEvent({
            event: '',
            currentStore,
            customerData,
            _ga: gaCookie || '',
        })

        gtm?.trackEvent({
            event: 'fireRemarketingTag',
            google_tag_params: {
                ecomm_pagetype: 'product',
                ecomm_prodid: product.id,
                ecomm_totalvalue: finalPrice,
                returnCustomer: await isUserLoggedIn(),
            },
        })
    }

    const relatedProductLoadedEvent = ({ products: impressions, ...params }: TypeRelatedProductLoadedEvent) => {
        gtm?.trackEvent({
            event: 'productsLoaded',
            ...params,
            ecommerce: {
                impressions,
            },
        })
    }

    const groupedProductLoadedEvent = async (
        parentProduct: ProductBase,
        products: [ProductBase],
        gaCookie: string,
        currentStore: string,
        customerData: BbkCustomerDataLayer
    ) => {
        const { isUserLoggedIn } = useUser()
        const { finalPrice, currencyCode } = usePrice(parentProduct)

        gtm?.trackEvent({
            event: '',
            pageType: 'product',
            currentStore,
            customerData,
            _ga: gaCookie || '',
        })

        gtm?.trackEvent({
            event: 'fireRemarketingTag',
            google_tag_params: {
                ecomm_pagetype: 'product',
                ecomm_prodid: parentProduct.id,
                ecomm_totalvalue: finalPrice,
                returnCustomer: await isUserLoggedIn(),
            },
        })

        gtm?.trackEvent({
            event: 'productDetail',
            ecommerce: {
                currencyCode,
                detail: {
                    products: products.map(product => {
                        return {
                            fullCategoryString: product?.dataLayer?.fullCategoryString,
                            topCategoryId: product?.dataLayer?.topCategoryId,
                            topCategoryName: product?.dataLayer?.topCategoryName,
                            url: `${siteUrl}/${currentStore}${getGtmProductUrl(product)}`,
                            seller: product.shopName || BRAND_BBK,
                            productType: product.typeId,
                            productFunctionPL: product.function,
                            properName: product.properName,
                            id: product.sku,
                            productId: product.id,
                            parentCategoryId: product.parentCategoryId || product.dlParentCategoryIdentifier || '',
                            parentCategoryName: product.parentCategoryName || product.dlParentCategoryName || '',
                            lowestPriceHistory: product.lowestPrice?.value || null,
                            name: product.name,
                            brand: product.brandLabel,
                            categoryId: product.categoryId || product.dlCategoryIdentifier || '',
                            categoryName: product.categoryName || product.dlCategoryName || '',
                            categoryHierarchyId: product.categoryHierarchyId || product.dlCategoryIdentifierPath || '',
                            backInStock: getGtmFlagIdentifier(product).includes('backInStock'),
                            bestseller: getGtmFlagIdentifier(product).includes('bestseller'),
                            freeShipping: getGtmFlagIdentifier(product).includes('free-delivery'),
                            inPromotion: getGtmFlagIdentifier(product).includes('discount'),
                            new: getGtmFlagIdentifier(product).includes('new'),
                            inStock: +product.inStock,
                            price: usePrice(product.productPrice).priceDiscount || usePrice(product.productPrice).regularPrice,
                            priceCatalog: usePrice(product.productPrice).regularPrice
                        }
                    })
                },
            },
        })
    }

    const carouselProductPageLoadedEvent = (products: ProductBase[], experienceId: string) => {
        gtm?.trackEvent({
            event: 'productsLoaded',
            eventOrigin: 'salesmanago',
            campaignId: 'frame',
            experienceId: experienceId,
            ecommerce: {
                impressions: {
                    promotions: products.map(product => {

                        const {regularPrice, priceDiscount } = usePrice(product.productPrice)

                        return {
                            productId: product.id,
                            id: product.sku,
                            name: product.name,
                            seller: product.shopName,
                            brand: product.brandLabel,
                            categoryId: product.dlCategoryIdentifier,
                            categoryName: product.dlCategoryName,
                            parentCategoryId: product.dlParentCategoryIdentifier,
                            parentCategoryName: product.dlParentCategoryName,
                            price: priceDiscount || regularPrice,
                            priceCatalog: regularPrice,
                            variant: ''
                        }
                    })
                }
            },
        })
    }

    return { carouselProductPageLoadedEvent, productPageLoadedEvent, relatedProductLoadedEvent, groupedProductLoadedEvent }
}
